import { InputDateModal } from "core/inputs/input-date/InputDateModal";
import InputSearchList from "core/inputs/input-search/InputSearchList";
import _ from "lodash";
import { FORM_INFO } from "constants/forms";
import { FormattedMessage } from "react-intl";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import { formatStaff } from "utils/modalsHelper";
import { phoneSize } from "utils/screenSizes";
import { useEffect, useState } from "react";
import {
  onAdd,
  onChange,
  onChangeInputManager,
  onChangeManager,
  onDelete,
  renderCommonBlock,
} from "utils/tableContentHelper";
import { headerGenerator } from "utils/HeaderHelper";
import Proptypes from "prop-types";
import AlertPopup from "components/alert-popup/AlertPopup";
import {
  FILTERS_FOR_NAMES,
  POPUP_STATUS,
  MODAL_CONTENT,
  QUERY_STATUS,
} from "constants/constants";
import { useUserContext } from "api/user/UserProvider";
import {
  ERROR_HISTORY_MANAGERS_ESTABLISHMENT_DATE_AFTER_MISSION_END,
  ERROR_HISTORY_MANAGERS_ESTABLISHMENT_DATE_BEFORE_MISSION_START,
  ERROR_HISTORY_MANAGERS_MISSING_INPUT,
  ERROR_HISTORY_MANAGERS_OUT_OF_MANAGER_CONCTRACT_ESTABLISHMENT_DATE,
} from "constants/popup-codes";
import ModalFooter from "components/modal/ModalFooter";
import { MISSION_MANAGER_ROLES } from "access-roles/roles";
import { format } from "date-fns";

const ManageHistoryManagers = ({
  historyManagers,
  usersByIds,
  lang,
  getUpdatedValues,
  onValidate,
  missionById,
  onClose,
  setElementsToDelete,
  isManagerActiveContract,
  getManagersContract,
  managersContracts,
  setManagersContracts,
  readOnly,
  getNamesByFilters,
  historyManagersNames,
}) => {
  const headerParams = [
    { key: "manager", defaultMessage: "Manager", icon: "user" },
    {
      key: "establishment_date",
      defaultMessage: "Date de prise en compte",
      icon: "calendar",
    },
    { key: "updated_by", defaultMessage: "Modifié par", icon: "user" },
    { key: "updated_at", defaultMessage: "le", icon: "calendar" },
  ];

  const header = headerGenerator(headerParams);
  const [staffList, setStaffList] = useState();
  const [formattedManager, setFormattedManager] = useState([]);
  const [initialData, setInitialData] = useState(historyManagers);
  const [data, setData] = useState(historyManagers);
  const [status, setStatus] = useState();
  const [code, setCode] = useState();
  const [isValidating, setIsValidating] = useState(false);
  const [dateErrorIds, setDateErrorIds] = useState([]);
  const { apiErrorResponse, setApiErrorResponse } = useUserContext();

  const setError = (code) => {
    setCode(code);
    setStatus(POPUP_STATUS.ERROR);
  };

  const getDateErrorIds = (rows) =>
    rows
      ?.filter(
        (e) =>
          e.establishment_date &&
          (e.establishment_date < missionById?.start_date ||
            e.establishment_date > missionById?.end_date)
      )
      .map((e) => e.id) ?? [];

  useEffect(() => {
    setApiErrorResponse(null);
    return () => setApiErrorResponse(null);
  }, []);

  useEffect(() => {
    if (!apiErrorResponse || Object.keys(apiErrorResponse).length === 0) return;
    const firstError = Object.values(apiErrorResponse)[0];
    const backCode =
      typeof firstError === "string" ? firstError : firstError?.code;
    if (backCode) {
      setError(backCode);
    } else {
      const fieldFallback = {
        establishment_date: "api_error_establishment_date",
        manager_id: "api_error_mgr",
      };
      const matchedField = Object.keys(fieldFallback).find(
        (field) => apiErrorResponse[field]
      );
      setError(matchedField ? fieldFallback[matchedField] : "api_error_field");
    }
    setIsValidating(false);
  }, [apiErrorResponse]);

  const checkInputs = (data) => {
    let error = null;
    data?.some((e) => {
      if (
        isManagerActiveContract({
          managerId: e.manager_id,
          establishmentDate: e.establishment_date,
          establishmentDateEnd: e.establishment_date_end,
        })
      ) {
        error =
          ERROR_HISTORY_MANAGERS_OUT_OF_MANAGER_CONCTRACT_ESTABLISHMENT_DATE;
        return true;
      }
      if (!e.manager_id || !e.establishment_date) {
        error = ERROR_HISTORY_MANAGERS_MISSING_INPUT;
        return true;
      }
      if (e.establishment_date < missionById?.start_date) {
        error = ERROR_HISTORY_MANAGERS_ESTABLISHMENT_DATE_BEFORE_MISSION_START;
        return true;
      }
      if (e.establishment_date > missionById?.end_date) {
        error = ERROR_HISTORY_MANAGERS_ESTABLISHMENT_DATE_AFTER_MISSION_END;
        return true;
      }
      return false;
    });
    return error;
  };

  useEffect(() => {
    if (
      managersContracts.length > 0 &&
      managersContracts.length === data.length
    ) {
      const error = checkInputs(data);
      if (error) {
        setError(error);
        setDateErrorIds(
          [
            ERROR_HISTORY_MANAGERS_ESTABLISHMENT_DATE_BEFORE_MISSION_START,
            ERROR_HISTORY_MANAGERS_ESTABLISHMENT_DATE_AFTER_MISSION_END,
          ].includes(error)
            ? getDateErrorIds(data)
            : []
        );
        setManagersContracts([]);
        setIsValidating(false);
        return;
      }
      onValidate();
      setManagersContracts([]);
    }
  }, [managersContracts]);

  const onClickValidate = async () => {
    if (isValidating) return;
    setCode();
    setStatus();
    setDateErrorIds([]);
    const earlyError = data?.some((e) => !e.manager_id || !e.establishment_date)
      ? ERROR_HISTORY_MANAGERS_MISSING_INPUT
      : null;
    if (earlyError) {
      setError(earlyError);
      return;
    }
    setApiErrorResponse(null);
    setIsValidating(true);
    try {
      await getManagersContract(data);
    } catch (e) {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    setStaffList((old) =>
      _.chain(old)
        .push(historyManagersNames)
        .flattenDeep()
        .uniqWith(_.isEqual)
        .value()
    );
  }, [historyManagersNames]);

  useEffect(() => {
    const formattedUsers = formatStaff(staffList);
    setFormattedManager(
      _.chain(formattedUsers)
        .concat(formattedManager)
        .flattenDeep()
        .uniqWith(_.isEqual)
        .value()
    );
  }, [staffList]);
  useEffect(() => {
    if (
      initialData.length < historyManagers.length &&
      historyManagers.length > 0
    ) {
      setInitialData(historyManagers);
    }
  }, [initialData, historyManagers]);

  useEffect(() => {
    if (_.isEqual(initialData, historyManagers) && initialData.length > 0) {
      setData(
        _.map(initialData, (elm) => ({
          ...elm,
          updated_by: usersByIds.find((usr) => usr.id === elm.updated_by)?.name,
        }))
      );
    }
  }, [initialData]);

  const formatContent = (content) =>
    content.length > 0
      ? content?.map((elm, index) => {
          const dataTestIndex = content.length - 1 - index;
          return {
            id: elm?.id,
            manager_id: (
              <InputSearchList
                limitChoice={1}
                readOnly={readOnly}
                onDelete={onDelete}
                options={formattedManager}
                popup={FORM_INFO.manager.popup}
                selectedOptions={formattedManager?.filter(
                  (br) => br.value === elm.manager_id
                )}
                minLength={3}
                getOptions={(search) =>
                  getNamesByFilters({
                    role: MISSION_MANAGER_ROLES,
                    types: [FILTERS_FOR_NAMES.PERSON],
                    search,
                  })
                }
                setValue={(val) => {
                  onChangeManager({
                    getUpdatedValues,
                    id: elm?.id,
                    key: "manager_id",
                    setData,
                    data,
                    val,
                    options: staffList,
                    managerId: elm?.manager_id,
                  });
                }}
                onChangeInput={() =>
                  onChangeInputManager({
                    getUpdatedValues,
                    id: elm?.id,
                    setData,
                    data,
                  })
                }
                dataTestId={`${MODAL_CONTENT.MANAGE_HISTORY_MANAGERS}_modal-manager-${dataTestIndex}`}
              />
            ),

            establishment_date: (
              <div
                data-testid={`${MODAL_CONTENT.MANAGE_HISTORY_MANAGERS}_modal-establishment_date-${dataTestIndex}`}
              >
                <InputDateModal
                  lang={lang}
                  elm={elm}
                  hasError={dateErrorIds.includes(elm?.id)}
                  onChange={(date) => {
                    const formattedDate = date
                      ? format(date, "yyyy-MM-dd")
                      : "";
                    setDateErrorIds((ids) =>
                      ids.filter((x) => x !== elm?.id)
                    );
                    onChange({
                      getUpdatedValues,
                      id: elm?.id,
                      key: "establishment_date",
                      setData,
                      data,
                      val: formattedDate,
                    });
                  }}
                />
              </div>
            ),
            ...renderCommonBlock({
              setElementsToDelete,
              data,
              readOnly: readOnly,
              getUpdatedValues,
              id: elm.id,
              onDelete,
              setData,
              lang,
              updated_at: elm.updated_at,
              updated_by: _.chain(historyManagers)
                .find((u) => u.id === elm.id)
                .get("updated_by", "")
                .value(),
              deleteBtnTestId: `${MODAL_CONTENT.MANAGE_HISTORY_MANAGERS}_modal-delete_btn-${dataTestIndex}`,
            }),
          };
        })
      : [];

  return (
    <>
      <button
        onClick={() => onAdd({ header, data, setData })}
        className="button-style"
        data-testid={`${MODAL_CONTENT.MANAGE_HISTORY_MANAGERS}_modal-add_manager_btn`}
      >
        <Typography
          className="link-1 violet "
          type="h2"
          label={
            <>
              + <FormattedMessage id="add" defaultMessage="Ajouter" />
            </>
          }
        />
      </button>
      <br />
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <Table
        id="manager-history"
        header={header}
        content={formatContent(data)}
        tableOverflow={"auto"}
        tableWidth={phoneSize ? "max-content" : "100%"}
      />
      <br />
      <br />
      <ModalFooter
        onClose={onClose}
        onValidate={() => onClickValidate()}
        modalType={MODAL_CONTENT.MANAGE_HISTORY_MANAGERS}
        disabled={isValidating ? QUERY_STATUS.LOADING : null}
      />
    </>
  );
};

export default ManageHistoryManagers;

ManageHistoryManagers.propTypes = {
  historyManagers: Proptypes.array,
  usersByIds: Proptypes.array,
  lang: Proptypes.string,
  getUpdatedValues: Proptypes.func,
  onValidate: Proptypes.func,
  missionById: Proptypes.object,
  onClose: Proptypes.func,
  setElementsToDelete: Proptypes.func,
  isManagerActiveContract: Proptypes.func,
  getManagersContract: Proptypes.func,
  managersContracts: Proptypes.array,
  setManagersContracts: Proptypes.func,
  readOnly: Proptypes.bool,
  getNamesByFilters: Proptypes.func,
  historyManagersNames: Proptypes.array,
};
